import { useLanguage } from "@/contexts/LanguageContext";
import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Square } from "lucide-react";
import { formatTime } from "@/utils/formatTime";
import { useIsMobile } from "@/hooks/use-mobile";
import PocketModeOverlay from "./PocketMode.Overlay";

interface RecordingInterfaceProps {
  isPaused: boolean;
  isSilenceWarningVisible: boolean;
  onTogglePause?: () => void;
  recordingDuration: number;
  canvasRef: React.RefObject<HTMLCanvasElement | null>;
  onStopRecording: () => void;
}

const RecordingInterface: React.FC<RecordingInterfaceProps> = ({
  isPaused,
  isSilenceWarningVisible,
  onTogglePause,
  recordingDuration,
  canvasRef,
  onStopRecording,
}) => {
  const { t } = useLanguage();
  const isMobile = useIsMobile();
  // Phones start in pocket mode so the screen stays on and touches are ignored
  const [isPocketMode, setIsPocketMode] = useState(true);
  useEffect(() => {
    if (canvasRef.current) {
      const canvas = canvasRef.current;
      const ctx = canvas.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = "#4f46e5";
      }
    }
  }, [canvasRef]);

  return (
    <div className="container mx-auto flex max-w-4xl flex-col items-center justify-center gap-6 p-6">
      <div className="flex items-center gap-2">
        <span className="size-2.5 rounded-full bg-red-500 animate-pulse" aria-hidden />
        <h1 className="text-sm font-medium text-chen-900">מקליט</h1>
        <span className="tabular-nums text-base font-semibold text-foreground">{formatTime(recordingDuration)}</span>
      </div>

      <canvas
        ref={canvasRef}
        width={280}
        height={60}
        className="w-full max-w-sm rounded-xl bg-muted/80"
      />

      {isPaused && <p role="status">{t('recordingSilence.paused')}</p>}
      {onTogglePause && <Button type="button" variant="outline" onClick={onTogglePause}>
        {t(isPaused ? 'recordingSilence.resume' : 'recordingSilence.pause')}
      </Button>}
      <Button
        type="button"
        onClick={onStopRecording}
        className="h-9 rounded-full bg-foreground px-4 font-semibold text-background hover:bg-foreground/90"
        aria-label="עצור הקלטה"
      >
        <Square className="size-3.5 fill-current" strokeWidth={2.25} aria-hidden />
        עצור הקלטה
      </Button>
      {isMobile && isPocketMode && (
        <PocketModeOverlay
          recordingDuration={recordingDuration}
          isSilenceWarningVisible={isSilenceWarningVisible}
          onExit={() => setIsPocketMode(false)}
        />
      )}
    </div>
  );
};

export default RecordingInterface;
